import { Reducer } from 'redux';
import { TAction } from '../../Resources/Model';
import { SearchObj, TNode } from '../CLDDocumentTree/@types';
import _ from 'lodash';
import { OCldDashboard } from './redux-config';
import { getStoredLegalFindingsFilter, getStoredDecisionsFilter } from './dashboard-utils';
import { appendChildren, NODE_LABEL_URL, removeChildren } from '../CLDDocumentTree/cld-documentTree-reducer';
import { OCLDDocumentTree } from '../CLDDocumentTree/redux-config';
import { TLog } from '../LtdDashboard/@types';
import { DEFAULT_ACTIVITY_LOG_FILTER, TActivityLogFilter } from './Filters/activityLog';
import { DEFAULT_ROLES_FILTER, TRoleFilter } from './Filters/roles';
import { TUser } from '../Authentication/@types';
import { ILegalFinding } from '../CLDDocuments/@types';
import { TDocument } from '../LTDDocuments/@types';
import { FacetAndCountList, IDecisionFilter, ILegalFindingsFilter } from './dashboard-types';

export interface IFacets {
    confidentiality: Array<{ key: string; doc_count: number }>;
    languageIds: Array<{ key: string; doc_count: number }>;
    _decision_typeOfDecisionId: Array<{ key: string; doc_count: number }>;
    source: Array<{ key: string; doc_count: number }>;
    _decision_situationNameCardId: Array<{ key: string; doc_count: number }>;
    _decision_caseNameCardId: Array<{ key: string; doc_count: number }>;
    _decision_ApplicableAppeal: FacetAndCountList<string | null>;
    legalFindings: Array<{ key: boolean | null; doc_count: number }>;
}

export interface ILegalFindingsFacets {
    progress: FacetAndCountList<string | null>;
    Confidentiality: FacetAndCountList<string | null>;
    Importance01: FacetAndCountList<string | null>;
    cldFieldLanguageId: FacetAndCountList<string | null>;
    ltdDoc_source: FacetAndCountList<string | null>;
    ltdDoc_decision_typeOfDecisionId: FacetAndCountList<string | null>;
    ltdDoc_decision_documentSourceId: FacetAndCountList<string | null>;
    ltdDoc_decision_situationNameCardId: FacetAndCountList<string | null>;
    ltdDoc_decision_caseNameCardId: FacetAndCountList<string | null>;
    ltdDoc_decision_ApplicableAppeal: FacetAndCountList<string | null>;
}

export interface TCldDashboardState {
    loading: boolean;
    tree: Array<TNode>;
    initialTree: Array<TNode>;
    itemsSelected: Array<string>;
    idsToSearch: Array<SearchObj>;
    keywordText: string;
    activityLog: {
        logs: TLog[];
        pageNumber: number;
        filter?: TActivityLogFilter;
    };
    roles: {
        users: TUser[];
        total: number;
        pageNumber: number;
        searchTerm: string;
        filter?: TRoleFilter;
    };
    decisions: {
        decisions: TDocument[];
        pageNumber: number;
        searchTerm: string;
        filter?: IDecisionFilter;
        total: number;
        isDownloading: boolean;
        hasMore: boolean;
        loading: boolean;
        facets: IFacets;
    };
    legalFindings: {
        legalFindings: Array<ILegalFinding>;
        pageNumber: number;
        searchTerm: string;
        filter?: ILegalFindingsFilter;
        total: number;
        isDownloading: boolean;
        hasMore: boolean;
        loading: boolean;
        facets: ILegalFindingsFacets;
    };
}

const initialState: TCldDashboardState = {
    loading: false,
    keywordText: '',
    idsToSearch: [],
    initialTree: [],
    itemsSelected: [],
    tree: [],
    activityLog: {
        logs: [],
        pageNumber: 0,
        filter: { ...DEFAULT_ACTIVITY_LOG_FILTER },
    },
    roles: {
        users: [],
        total: 0,
        pageNumber: 0,
        searchTerm: '',
        filter: { ...DEFAULT_ROLES_FILTER },
    },
    decisions: {
        decisions: [],
        pageNumber: 0,
        searchTerm: '',
        filter: getStoredDecisionsFilter(),
        total: 0,
        isDownloading: false,
        hasMore: false,
        loading: false,
        facets: {
            confidentiality: [],
            languageIds: [],
            _decision_typeOfDecisionId: [],
            source: [],
            _decision_situationNameCardId: [],
            _decision_caseNameCardId: [],
            _decision_ApplicableAppeal: [],
            legalFindings: [],
        },
    },
    legalFindings: {
        legalFindings: [],
        pageNumber: 0,
        searchTerm: '',
        filter: getStoredLegalFindingsFilter(),
        total: 0,
        isDownloading: false,
        hasMore: false,
        loading: false,
        facets: {
            progress: [],
            Confidentiality: [],
            Importance01: [],
            cldFieldLanguageId: [],
            ltdDoc_source: [],
            ltdDoc_decision_typeOfDecisionId: [],
            ltdDoc_decision_documentSourceId: [],
            ltdDoc_decision_situationNameCardId: [],
            ltdDoc_decision_caseNameCardId: [],
            ltdDoc_decision_ApplicableAppeal: [],
        },
    },
};

export const CLD_DASHBOARD_ROLES_USERS_LISTING_LIMIT = 20;

const cldDashboardReducer: Reducer<TCldDashboardState, TAction> = (state = initialState, action) => {
    const {
        CLD_DASHBOARD_FETCHED_CHILDREN,
        CLD_DASHBOARD_REMOVED_CHILDREN,
        CLD_DASHBOARD_RESET_TREE,
        CLD_DASHBOARD_TREE_ITEM_UNSELECTED,
        CLD_DASHBOARD_NEW_TREE_FETCHED,
        CLD_DASHBOARD_SET_LOADING,
        CLD_DASHBOARD_SET_KEYWORD_TEXT,
        CLD_DASHBOARD_ADDED_NEW_NODE,
        CLD_DASHBOARD_UPDATED_NODE,
        CLD_DASHBOARD_DELETED_NODE,
        // LOGS
        CLD_DASHBOARD_LOG_ACTIVITIES_FETCHED,
        CLD_DASHBOARD_LOG_STATUS_UPDATED,
        CLD_DASHBOARD_LOG_ACTIVITY_DOC_PATH_UPDATED,
        CLD_DASHBOARD_ACTIVITY_LOGS_SET_FILTER,
        // ROLES
        CLD_DASHBOARD_ROLES_FETCHED_USERS,
        CLD_DASHBOARD_ROLES_SET_SEARCH_TERM,
        CLD_DASHBOARD_ROLES_SET_PAGE_NUMBER,
        CLD_DASHBOARD_ROLES_UPDATE,
        CLD_DASHBOARD_ROLES_SET_FILTER,
        // DECISIONS
        CLD_DASHBOARD_DECISIONS_FETCHED,
        CLD_DASHBOARD_DECISIONS_SET_FILTER,
        CLD_DASHBOARD_DECISIONS_SET_PAGE_NUMBER,
        CLD_DASHBOARD_DECISIONS_SET_SEARCH_TERM,
        CLD_DASHBOARD_DECISIONS_SET_LOADING,
        CLD_DASHBOARD_DECISIONS_SET_DOWNLOADING,
        CLD_DASHBOARD_DECISIONS_INITIAL_FACETS_FETCHED,
        // LEGAL FINDINGS
        CLD_DASHBOARD_LEGAL_FINDINGS_FETCHED,
        CLD_DASHBOARD_LEGAL_FINDINGS_SET_FILTER,
        CLD_DASHBOARD_LEGAL_FINDINGS_SET_PAGE_NUMBER,
        CLD_DASHBOARD_LEGAL_FINDINGS_SET_SEARCH_TERM,
        CLD_DASHBOARD_LEGAL_FINDINGS_SET_DOWNLOADING,
        CLD_DASHBOARD_LEGAL_FINDINGS_SET_LOADING,
        CLD_DASHBOARD_LEGAL_FINDINGS_INITIAL_FACETS_FETCHED,
    } = OCldDashboard.actions;

    const { CLD_FETCHED_TREE_NODES } = OCLDDocumentTree.actions;

    switch (action.type) {
        case CLD_DASHBOARD_ROLES_UPDATE: {
            let updated_users = _.cloneDeep(state.roles.users);
            if (action.data.action === 'add') {
                updated_users = updated_users.map(user => {
                    if ((action.data.ids || []).includes(user.id)) {
                        user.roles.push({
                            name: action.data.role,
                        });
                    }
                    return user;
                });
            } else {
                updated_users = updated_users.map(user => {
                    if ((action.data.ids || []).includes(user.id)) {
                        user.roles = user.roles.filter(role => role.name !== action.data.role);
                    }
                    return user;
                });
            }
            return {
                ...state,
                roles: {
                    ...state.roles,
                    users: updated_users,
                },
            };
        }
        case CLD_DASHBOARD_ROLES_SET_PAGE_NUMBER: {
            return {
                ...state,
                roles: {
                    ...state.roles,
                    pageNumber: action.data,
                },
            };
        }
        case CLD_DASHBOARD_ROLES_SET_SEARCH_TERM: {
            return {
                ...state,
                roles: {
                    ...state.roles,
                    searchTerm: action.data,
                },
            };
        }
        case CLD_DASHBOARD_ROLES_FETCHED_USERS: {
            return {
                ...state,
                roles: {
                    ...state.roles,
                    ...action.data,
                },
            };
        }
        // TREE MANAGEMENT
        case CLD_DASHBOARD_UPDATED_NODE: {
            return {
                ...state,
            };
        }
        case CLD_DASHBOARD_DELETED_NODE: {
            return {
                ...state,
            };
        }
        case CLD_DASHBOARD_ADDED_NEW_NODE: {
            return {
                ...state,
            };
        }
        case CLD_DASHBOARD_SET_KEYWORD_TEXT:
            return {
                ...state,
                keywordText: action.data,
            };
        case CLD_DASHBOARD_SET_LOADING: {
            return {
                ...state,
                loading: action.data,
            };
        }

        case CLD_DASHBOARD_NEW_TREE_FETCHED: {
            return {
                ...state,
                tree: action.data,
            };
        }
        case CLD_DASHBOARD_TREE_ITEM_UNSELECTED:

        case CLD_DASHBOARD_RESET_TREE: {
            return {
                ...state,
                tree: _.cloneDeep(state.initialTree),
            };
        }
        case CLD_DASHBOARD_REMOVED_CHILDREN: {
            return {
                ...state,
                tree: removeChildren(state.tree, action.data),
            };
        }
        case CLD_DASHBOARD_FETCHED_CHILDREN: {
            const children = _.map(action.data.children, node => {
                node.hasChildrenMenuNode = !!node.childrenIds.length;
                return node;
            });
            return {
                ...state,
                tree: appendChildren(state.tree, children, action.data.parentId),
            };
        }
        case CLD_FETCHED_TREE_NODES: {
            const tree = _.map(action.data, node => {
                if (node.nodeType === 'LABEL')
                    return {
                        ...node,
                        url: NODE_LABEL_URL[node.keyword_text],
                    };
                else node.hasChildrenMenuNode = !!node.childrenIds.length;
                return node;
            });
            // here we store a copy of initial tree just in case the user wants to collapse/reset the tree
            return {
                ...state,
                tree,
                initialTree: _.cloneDeep(tree),
            };
        }

        // ACTIVITY LOGS
        case CLD_DASHBOARD_LOG_ACTIVITIES_FETCHED: {
            const { appendResult = true } = action.data;
            return {
                ...state,
                activityLog: {
                    ...state.activityLog,
                    logs: appendResult ? [...state.activityLog.logs, ...action.data.logs] : action.data.logs || [],
                    pageNumber: action.data.pageNumber,
                },
            };
        }
        case CLD_DASHBOARD_LOG_STATUS_UPDATED: {
            if (_.isEmpty(action.data.logs) || !action.data.status) return state;
            let updatedActivityLogs: TLog[] = state.activityLog.logs.map<TLog>(log => {
                const _log = _.find(_.get(action.data, 'logs'), {
                    id: log.id,
                });
                const update = _.find(_.get(action.data, 'logUpdates'), {
                    id: log.id,
                });
                if (update || _log)
                    return {
                        ..._log,
                        ...update,
                        status: action.data.status,
                    };
                return log;
            });

            return {
                ...state,
                activityLog: {
                    ...state.activityLog,
                    logs: updatedActivityLogs,
                },
            };
        }
        case CLD_DASHBOARD_LOG_ACTIVITY_DOC_PATH_UPDATED: {
            if (_.isEmpty(_.get(action, 'data.docPath')) || !_.get(action, 'data.logId')) return state;

            const logIndex = state.activityLog.logs.findIndex(log => log.id === action.data.logId);

            if (logIndex === -1) return state; // Log not found

            const logToUpdate = state.activityLog.logs[logIndex];
            const updateDocKey = action.data.updateDocKey || 'subject';

            const updatedLog = _.cloneDeep(logToUpdate);
            const documentPart = _.get(updatedLog, updateDocKey);

            if (documentPart && typeof documentPart === 'object') {
                _.set(documentPart, 'docPath', action.data.docPath);
            } else {
                _.set(updatedLog, updateDocKey, {
                    ...(typeof documentPart === 'object' ? documentPart : {}),
                    docPath: action.data.docPath,
                });
            }

            const newLogs = [
                ...state.activityLog.logs.slice(0, logIndex),
                updatedLog,
                ...state.activityLog.logs.slice(logIndex + 1),
            ];

            return {
                ...state,
                activityLog: {
                    ...state.activityLog,
                    logs: newLogs,
                },
            };
        }
        case CLD_DASHBOARD_ACTIVITY_LOGS_SET_FILTER: {
            return {
                ...state,
                activityLog: {
                    ...state.activityLog,
                    filter: action.data,
                },
            };
        }
        case CLD_DASHBOARD_ROLES_SET_FILTER: {
            return {
                ...state,
                roles: {
                    ...state.roles,
                    filter: action.data,
                },
            };
        }

        // DECISIONS
        case CLD_DASHBOARD_DECISIONS_INITIAL_FACETS_FETCHED: {
            const newGlobalFacetsData = action.data.facets || {};
            const fullyPopulatedGlobalFacets: Partial<IFacets> = {};
            for (const key of Object.keys(initialState.decisions.facets)) {
                fullyPopulatedGlobalFacets[key] = newGlobalFacetsData[key] || [];
            }
            return {
                ...state,
                decisions: {
                    ...state.decisions,
                    facets: fullyPopulatedGlobalFacets as IFacets,
                },
            };
        }
        case CLD_DASHBOARD_DECISIONS_FETCHED: {
            const currentDecisions =
                action.data.pageNumber === 0
                    ? action.data.decisions || []
                    : [...(state.decisions.decisions || []), ...(action.data.decisions || [])];

            const hasMore = (action.data.decisions || []).length === action.data.limit;

            const updatedFacets = { ...state.decisions.facets };
            const newApiFacets = action.data.facets || {};

            // Helper function to normalize array keys for comparison
            const normalizeArrayKey = (key: any): string => {
                if (key === null || key === undefined || key === '') return String(key);
                
                // Handle array keys
                if (Array.isArray(key)) {
                    return key.map(k => String(k).trim()).sort().join(',');
                }
                
                // Handle comma-separated string keys
                const keyStr = String(key);
                if (keyStr.includes(',')) {
                    return keyStr.split(',').map(s => s.trim()).sort().join(',');
                }
                
                return keyStr;
            };

            for (const categoryKey of Object.keys(updatedFacets)) {
                const currentItems = updatedFacets[categoryKey] || [];
                const newApiItemsForCategory = newApiFacets[categoryKey] || [];
                
                const resultItemsForCategory: Array<{ key: any; doc_count: number }> = [];
                
                // Check if any items in this category have array or comma-separated keys
                const hasArrayKeys = [...currentItems, ...newApiItemsForCategory].some(item => 
                    Array.isArray(item.key) || (typeof item.key === 'string' && item.key.includes(','))
                );
                
                if (hasArrayKeys) {
                    // Use normalized keys for deduplication when array keys are present
                    const keyMap = new Map<string, { key: any; doc_count: number }>();
                    
                    // Process current items
                    currentItems.forEach(item => {
                        const normalizedKey = normalizeArrayKey(item.key);
                        keyMap.set(normalizedKey, { key: item.key, doc_count: 0 }); // Reset count, will be updated from API
                    });
                    
                    // Process new API items (these have the current counts)
                    newApiItemsForCategory.forEach(item => {
                        const normalizedKey = normalizeArrayKey(item.key);
                        keyMap.set(normalizedKey, { key: item.key, doc_count: item.doc_count });
                    });
                    
                    resultItemsForCategory.push(...keyMap.values());
                } else {
                    const allKeys = new Set([...currentItems.map(i => i.key), ...newApiItemsForCategory.map(i => i.key)]);
                    
                    allKeys.forEach(key => {
                        const apiItemData = newApiItemsForCategory.find(i => i.key === key);
                        const doc_count = apiItemData ? apiItemData.doc_count : 0;
                        resultItemsForCategory.push({ key, doc_count });
                    });
                }
                
                updatedFacets[categoryKey] = resultItemsForCategory;
            }

            return {
                ...state,
                decisions: {
                    ...state.decisions,
                    decisions: currentDecisions,
                    pageNumber: action.data.pageNumber,
                    hasMore,
                    total: action.data.total || 0,
                    facets: updatedFacets,
                },
            };
        }
        case CLD_DASHBOARD_DECISIONS_SET_FILTER: {
            return {
                ...state,
                decisions: {
                    ...state.decisions,
                    filter: action.data,
                    hasMore: true,
                },
            };
        }
        case CLD_DASHBOARD_DECISIONS_SET_PAGE_NUMBER: {
            return {
                ...state,
                decisions: {
                    ...state.decisions,
                    pageNumber: action.data,
                },
            };
        }
        case CLD_DASHBOARD_DECISIONS_SET_SEARCH_TERM: {
            return {
                ...state,
                decisions: {
                    ...state.decisions,
                    searchTerm: action.data,
                },
            };
        }
        case CLD_DASHBOARD_DECISIONS_SET_DOWNLOADING: {
            return {
                ...state,
                decisions: {
                    ...state.decisions,
                    isDownloading: action.data,
                },
            };
        }
        case CLD_DASHBOARD_DECISIONS_SET_LOADING: {
            return {
                ...state,
                decisions: {
                    ...state.decisions,
                    loading: action.data,
                },
            };
        }

        // LEGAL FINDINGS
        case CLD_DASHBOARD_LEGAL_FINDINGS_INITIAL_FACETS_FETCHED: {
            const newGlobalFacetsData = action.data.facets || {};
            const fullyPopulatedGlobalFacets: Partial<ILegalFindingsFacets> = {};
            for (const key of Object.keys(initialState.legalFindings.facets)) {
                fullyPopulatedGlobalFacets[key] = newGlobalFacetsData[key] || [];
            }
            return {
                ...state,
                legalFindings: {
                    ...state.legalFindings,
                    facets: fullyPopulatedGlobalFacets as ILegalFindingsFacets,
                },
            };
        }
        case CLD_DASHBOARD_LEGAL_FINDINGS_FETCHED: {
            const currentFindings =
                action.data.pageNumber === 0
                    ? action.data.legalFindings || []
                    : [...(state.legalFindings.legalFindings || []), ...(action.data.legalFindings || [])];

            const hasMore = (action.data.legalFindings || []).length === action.data.limit;

            const updatedFacets = { ...state.legalFindings.facets };
            const newApiFacets = action.data.facets || {};

            for (const categoryKey of Object.keys(updatedFacets)) {
                const currentItems = updatedFacets[categoryKey] || [];
                const newApiItemsForCategory = newApiFacets[categoryKey] || [];

                const resultItemsForCategory: Array<{ key: any; doc_count: number }> = [];
                const allKeys = new Set([...currentItems.map(i => i.key), ...newApiItemsForCategory.map(i => i.key)]);

                allKeys.forEach(key => {
                    const apiItemData = newApiItemsForCategory.find(i => i.key === key);
                    const doc_count = apiItemData ? apiItemData.doc_count : 0;
                    resultItemsForCategory.push({ key, doc_count });
                });
                updatedFacets[categoryKey] = resultItemsForCategory;
            }

            return {
                ...state,
                legalFindings: {
                    ...state.legalFindings,
                    legalFindings: currentFindings,
                    pageNumber: action.data.pageNumber,
                    hasMore,
                    total: action.data.total || 0,
                    facets: updatedFacets,
                },
            };
        }
        case CLD_DASHBOARD_LEGAL_FINDINGS_SET_FILTER: {
            return {
                ...state,
                legalFindings: {
                    ...state.legalFindings,
                    filter: action.data,
                    hasMore: true,
                },
            };
        }
        case CLD_DASHBOARD_LEGAL_FINDINGS_SET_PAGE_NUMBER: {
            return {
                ...state,
                legalFindings: {
                    ...state.legalFindings,
                    pageNumber: action.data,
                },
            };
        }
        case CLD_DASHBOARD_LEGAL_FINDINGS_SET_SEARCH_TERM: {
            return {
                ...state,
                legalFindings: {
                    ...state.legalFindings,
                    searchTerm: action.data,
                },
            };
        }
        case CLD_DASHBOARD_LEGAL_FINDINGS_SET_DOWNLOADING: {
            return {
                ...state,
                legalFindings: {
                    ...state.legalFindings,
                    isDownloading: action.data,
                },
            };
        }
        case CLD_DASHBOARD_LEGAL_FINDINGS_SET_LOADING: {
            return {
                ...state,
                legalFindings: {
                    ...state.legalFindings,
                    loading: action.data,
                },
            };
        }

        default:
            return {
                ...state,
            };
    }
};

export default cldDashboardReducer;
